/**
 * WeaveIn.ai Platform - Workflow Instance Hook
 * React hooks for workflow instance management and step execution
 */

import { useState, useEffect, useCallback } from 'react';
import {
  workflowsAPI,
  WorkflowInstance,
  WorkflowInstanceDetail,
  WorkflowTemplate,
  StepExecuteAction,
  StepExecuteResponse,
  StepInstanceStatus,
} from '@/services/api/workflows';

// ============================================================================
// useWorkflowInstance - Single instance detail and step execution
// ============================================================================

interface UseWorkflowInstanceReturn {
  instance: WorkflowInstanceDetail | null;
  loading: boolean;
  error: string | null;
  executeStep: (
    stepId: number,
    action: StepExecuteAction,
    notes?: string,
    resultData?: Record<string, any>
  ) => Promise<StepExecuteResponse | null>;
  refetch: () => Promise<void>;
}

export default function useWorkflowInstance(
  instanceId: number | null | undefined
): UseWorkflowInstanceReturn {
  const [instance, setInstance] = useState<WorkflowInstanceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInstance = useCallback(async () => {
    if (!instanceId) {
      setInstance(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await workflowsAPI.getWorkflowInstanceDetails(instanceId);
      setInstance(data);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to fetch workflow instance';
      setError(errorMessage);
      console.error('Error fetching workflow instance:', err);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    fetchInstance();
  }, [fetchInstance]);

  const executeStep = async (
    stepId: number,
    action: StepExecuteAction,
    notes?: string,
    resultData?: Record<string, any>
  ): Promise<StepExecuteResponse | null> => {
    if (!instanceId) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await workflowsAPI.executeWorkflowStep(instanceId, stepId, {
        action,
        notes,
        result_data: resultData,
      });

      // Refetch to get updated state - the response doesn't include full step data
      await fetchInstance();

      return response;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to execute workflow step';
      setError(errorMessage);
      console.error('Error executing workflow step:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    instance,
    loading,
    error,
    executeStep,
    refetch: fetchInstance,
  };
}

// ============================================================================
// useEnrollmentWorkflows - Get workflows for an enrollment
// ============================================================================

interface UseEnrollmentWorkflowsReturn {
  instances: WorkflowInstance[];
  templates: WorkflowTemplate[];
  loading: boolean;
  error: string | null;
  createInstance: (templateId: number) => Promise<WorkflowInstance | null>;
  refetch: () => Promise<void>;
}

export function useEnrollmentWorkflows(
  enrollmentId: number | null | undefined,
  programId?: number | null
): UseEnrollmentWorkflowsReturn {
  const [instances, setInstances] = useState<WorkflowInstance[]>([]);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!enrollmentId) {
      setInstances([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch workflow instances for this enrollment
      const instancesData = await workflowsAPI.getEnrollmentWorkflowInstances(enrollmentId);
      setInstances(instancesData);

      // Fetch available workflow templates for the program
      if (programId) {
        const templatesResponse = await workflowsAPI.listWorkflowTemplates({
          program_id: programId,
          is_active: true,
        });
        setTemplates(templatesResponse.workflows || []);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to fetch enrollment workflows';
      setError(errorMessage);
      console.error('Error fetching enrollment workflows:', err);
    } finally {
      setLoading(false);
    }
  }, [enrollmentId, programId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createInstance = async (templateId: number): Promise<WorkflowInstance | null> => {
    if (!enrollmentId) return null;

    setLoading(true);
    setError(null);

    try {
      const newInstance = await workflowsAPI.createWorkflowInstance({
        workflow_template_id: templateId,
        enrollment_id: enrollmentId,
      });

      setInstances((prev) => [newInstance, ...prev]);
      return newInstance;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to create workflow instance';
      setError(errorMessage);
      console.error('Error creating workflow instance:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    instances,
    templates,
    loading,
    error,
    createInstance,
    refetch: fetchData,
  };
}
